import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { DocumentChartBarIcon, Cog8ToothIcon } from '@heroicons/react/24/outline';
import { ROUTES } from '../../consts/general';
import ReportTemplate from '../../components/reports/ReportTemplate';
import DateRangePicker from '../../components/reports/DateRangePicker';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { ReportEmailSettingsSidebar } from '../../components/reports/ReportEmailSettingsSidebar';
import { SHOPIFY_PROFIT_BY_SKU_SUMMARY } from '../../consts/reports';
import { SHOPIFY_PROFIT_BY_SKU_SUMMARY_TYPE } from '../../consts/cronTypes';
import { useAbortableRequest } from '../../helpers/useAbortableRequest';
import { useInitialMountDelay } from '../../helpers/useInitialMountDelay';
import {
  getReportEmailSettings,
  updateReportEmailSettings,
  sendReportPreviewEmail,
} from '../../slices/reportEmailSettings';
import { getReportSendHistory } from '../../slices/reportSendHistory';
import { updateCronInformation } from '../../slices/cronSlice';
import { buildCronInterval } from '../../helpers/cronExpression';
import { toast } from 'react-toastify';
import useDateRangeParams from '../../hooks/useDateRangeParams';

const DEFAULT_END_DATE = new Date().toISOString().split('T')[0];
const DEFAULT_START_DATE = (() => {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return date.toISOString().split('T')[0];
})();

const ShopifyProfitBySkuSummaryReport = ({
  marginFromSidebar
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { settings: allSettings, loading: isLoadingEmailSettings } = useSelector((s) => s.reportEmailSettings);
  const emailSettings = allSettings['shopify-profit-by-sku-summary'] || {};
  const { sendHistory: allSendHistory, loading: isLoadingSendHistory } = useSelector((s) => s.reportSendHistory);
  const sendHistory = allSendHistory['shopify-profit-by-sku-summary'] || [];

  const [reportData, setReportData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const pageSize = 50;

  const { startDate, endDate, setDateRange } = useDateRangeParams(DEFAULT_START_DATE, DEFAULT_END_DATE);

  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [isOpenSettings, setIsOpenSettings] = useState(false);
  const { beginRequest, isLatest } = useAbortableRequest();
  const initialReady = useInitialMountDelay(500);

  useEffect(() => {
    fetchReportMetadata();
    dispatch(getReportEmailSettings('shopify-profit-by-sku-summary'));
    dispatch(getReportSendHistory('shopify-profit-by-sku-summary'));
  }, [dispatch]);

  useEffect(() => {
    if (!initialReady) return;
    if (startDate && endDate) {
      fetchReportData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialReady, startDate, endDate, currentPage, sortField, sortDirection]);

  const fetchReportMetadata = async () => {
    try {
      const response = await fetch(`/api/v1/reports/${SHOPIFY_PROFIT_BY_SKU_SUMMARY}/metadata`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch report metadata');
      }
      const data = await response.json();
      setColumns((data.columns || []).map((column, index) => {
        if (index === 0) {
          return {
            ...column,
            sticky: true
          }
        }
        return column
      }));
    } catch (err) {
      console.error('Error fetching metadata:', err);
      setError('Failed to load report configuration');
    }
  };

  const fetchReportData = async () => {
    if (!startDate || !endDate) return;
    const { signal, requestId } = beginRequest();
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        startDate,
        endDate,
        page: currentPage.toString(),
        pageSize: pageSize.toString()
      });
      if (sortField) {
        params.append('sortField', sortField);
        params.append('sortDirection', sortDirection);
      }
      const response = await fetch(`/api/v1/reports/${SHOPIFY_PROFIT_BY_SKU_SUMMARY}?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        signal
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch report data');
      }
      const data = await response.json();
      if (!isLatest(requestId)) return;
      setReportData(data.data || []);
      setTotalRecords(data.totalRecords || 0);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('Error fetching report data:', err);
      if (!isLatest(requestId)) return;
      setError(err.message);
      setReportData([]);
    } finally {
      if (isLatest(requestId)) setIsLoading(false);
    }
  };

  const handleDateChange = ({ startDate: newStartDate, endDate: newEndDate }) => {
    setDateRange({ startDate: newStartDate, endDate: newEndDate });
    setCurrentPage(1);
    setSortField(null);
    setSortDirection(null);
  };

  const handleSort = ({ field, direction }) => {
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const handleBackToReports = () => {
    navigate(ROUTES.REPORTS);
  };

  const handleExportCSV = async () => {
    if (!startDate || !endDate) {
      throw new Error('Please select a date range first');
    }
    const params = new URLSearchParams({ startDate, endDate });
    if (sortField) {
      params.append('sortField', sortField);
      params.append('sortDirection', sortDirection);
    }
    const response = await fetch(`/api/v1/reports/${SHOPIFY_PROFIT_BY_SKU_SUMMARY}/export?${params}`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Export failed');
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const contentDisposition = response.headers.get('content-disposition');
    const filename = contentDisposition ? contentDisposition.split('filename=')[1]?.replace(/"/g, '') : `shopify-profit-by-sku-summary_${new Date().toISOString().slice(0,10)}.csv`;
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    return { success: true };
  };

  const breadcrumbs = [
    { label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> },
    { label: '' }
  ];

  const filtersComponent = (
    <DateRangePicker
      startDate={startDate}
      endDate={endDate}
      onDateChange={handleDateChange}
      label="Date Range"
      error={error && error.includes('date') ? error : null}
    />
  );

  const exportButton = (
    <CSVExportButton
      data={[]}
      columns={columns}
      filename={`${SHOPIFY_PROFIT_BY_SKU_SUMMARY}`}
      onExport={handleExportCSV}
      disabled={!startDate || !endDate || isLoading}
      variant="outline"
    />
  );

  const paginationComponent = totalPages > 1 ? (
    <div className="flex items-center justify-between">
      <div className="text-sm text-gray-700">
        Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords} results
      </div>
      <div className="flex items-center space-x-2">
        <button onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50">Previous</button>
        <span className="text-sm text-gray-700">Page {currentPage} of {totalPages}</span>
        <button onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50">Next</button>
      </div>
    </div>
  ) : null;

  const handleOpenSettings = () => setIsOpenSettings(true);

  const handleSuccessSubmit = async (formData) => {
    const payload = {
      emailRecipients: formData?.emailRecipients || [],
      scheduleType: formData?.schedule || null,
      dayOfMonth: formData?.dayOfMonth || null,
      slackChannelId: formData?.slackChannelId || null,
      dayOfWeek: formData?.dayOfWeek || null,
      timeOfDay: formData?.timeOfDay || null,
      emailMessage: formData?.emailMessage || null,
    };
    try {
      await dispatch(updateReportEmailSettings({ reportType: 'shopify-profit-by-sku-summary', ...payload })).unwrap();
      const cronInterval = buildCronInterval({
        scheduleType: payload.scheduleType,
        dayOfMonth: payload.dayOfMonth,
        dayOfWeek: payload.dayOfWeek,
        timeOfDay: payload.timeOfDay,
      });
      if (!cronInterval) {
        toast.error('Invalid schedule settings (cron expression could not be built)', { position: 'bottom-right' });
        return;
      }
      const cronData = {
        id: emailSettings?.cronId,
        type: emailSettings?.cronType,
        cronInterval,
      };
      await dispatch(updateCronInformation({ singleData: cronData })).unwrap();
      toast.success('Settings saved successfully', { position: 'bottom-right' });
      setIsOpenSettings(false);
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to save settings', { position: 'bottom-right' });
    }
  };

  const sendPreviewEmail = async (formData) => {
    try {
      const payload = {
        email: formData?.emailSendPreviewTo,
        emailMessage: formData?.emailMessage || '',
        scheduleType: formData?.type || null,
        startDate,
        endDate,
        sortField,
        sortDirection,
      };
      await dispatch(sendReportPreviewEmail({ reportType: 'shopify-profit-by-sku-summary', ...payload })).unwrap();
      await dispatch(getReportSendHistory('shopify-profit-by-sku-summary')).unwrap();
      toast.success('Preview email sent', { position: 'bottom-right' });
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to send preview', { position: 'bottom-right' });
    }
  };

  const sendPreviewSlack = async (formData) => {
    try {
      const payload = {
        slackChannelId: formData?.slackChannelId,
        emailMessage: formData?.emailMessage || '',
        scheduleType: formData?.type || null,
        startDate,
        endDate,
        sortField,
        sortDirection,
      };
      await dispatch(sendReportPreviewEmail({ reportType: 'shopify-profit-by-sku-summary', ...payload })).unwrap();
      await dispatch(getReportSendHistory('shopify-profit-by-sku-summary')).unwrap();
      toast.success('Slack preview sent', { position: 'bottom-right' });
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to send slack preview', { position: 'bottom-right' });
    }
  };

  const renderTableRows = () => {
    return reportData.map((row, index) => (
      <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
        <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${index % 2 === 0 ? 'bg-white' : '!bg-gray-50'}`}>{row.sku}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.product}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{row.revenue_percentage}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{row.aov}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{row.orders}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{row.cogs}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.cogs_basis === 'virtual' ? 'Virtual ($0)' : row.cogs_basis}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{row.label_cost}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{row.packaging_cost}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{row.processing_fees}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{row.total_cost}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{row.gross_sales}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{row.discounts}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{row.returns}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{row.net_sales}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{row.beroas}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{row.gross_profit}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{row.gp_per_order}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{row.gp_percent}</td>
      </tr>
    ));
  };

  return (
    <>
      <ReportTemplate
        reportId={SHOPIFY_PROFIT_BY_SKU_SUMMARY}
        description="Profit metrics by SKU for Shopify orders"
        breadcrumbs={breadcrumbs}
        marginFromSidebar={marginFromSidebar}
        onBack={handleBackToReports}
        isLoading={isLoading}
        error={error}
        filters={filtersComponent}
        exportButton={
          <div className="flex items-center space-x-2">
            {exportButton}
            <div>
              <Cog8ToothIcon
                className="h-5 w-5 text-primary cursor-pointer"
                onClick={() => {
                  setIsOpenSettings(true);
                }}
              />
            </div>
          </div>
        }
        data={reportData}
        cronType={emailSettings?.cronType || null}
        columns={columns}
        pagination={paginationComponent}
        emptyMessage="No data found for the selected date range."
        onSort={handleSort}
        sortField={sortField}
        sortDirection={sortDirection}
        enableSorting={true}
      >
        {renderTableRows()}
      </ReportTemplate>
      <ReportEmailSettingsSidebar
        reportType={'shopify-profit-by-sku-summary'}
        isOpen={isOpenSettings}
        marginFromSidebar={marginFromSidebar}
        onClose={() => setIsOpenSettings(false)}
        isLoading={isLoadingEmailSettings}
        onSuccessSubmit={handleSuccessSubmit}
        sendPreviewEmail={sendPreviewEmail}
        sendPreviewSlack={sendPreviewSlack}
        defaultSettings={emailSettings || {}}
        sendHistory={sendHistory}
        isLoadingSendHistory={isLoadingSendHistory}
        downloadFilenamePrefix="shopify-profit-by-sku-summary"
      />
    </>
  );
};

export default ShopifyProfitBySkuSummaryReport;
